import React, { Suspense, lazy } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/contexts/AuthContext";
import { I18nProvider } from "@/contexts/I18nContext";
import { PublicConfigProvider } from "@/contexts/PublicConfigContext";
import ErrorBoundary from "@/components/ErrorBoundary";
import I18nErrorBoundary from "@/components/I18nErrorBoundary";
import ProtectedRoute from "@/components/ProtectedRoute";

const LoginPage = lazy(() => import("@/pages/LoginPage"));
const HomePage = lazy(() => import("@/pages/HomePage"));
const PublishSelectPage = lazy(() => import("@/pages/PublishSelectPage"));
const PublishItemPage = lazy(() => import("@/pages/PublishItemPage"));
const MyItemsPage = lazy(() => import("@/pages/MyItemsPage"));
const ItemDetailPage = lazy(() => import("@/pages/ItemDetailPage"));
const MatchDetailPage = lazy(() => import("@/pages/MatchDetailPage"));
const ChatListPage = lazy(() => import("@/pages/ChatListPage"));
const ChatPage = lazy(() => import("@/pages/ChatPage"));
const NotificationsPage = lazy(() => import("@/pages/NotificationsPage"));
const ProfilePage = lazy(() => import("@/pages/ProfilePage"));
const SettingsPage = lazy(() => import("@/pages/SettingsPage"));
const PaymentSuccessPage = lazy(() => import("@/pages/PaymentSuccessPage"));
const PickupPage = lazy(() => import("@/pages/PickupPage"));
const SignaturePage = lazy(() => import("@/pages/SignaturePage"));
const ClaimIdentifiedPage = lazy(() => import("@/pages/ClaimIdentifiedPage"));
const ClaimInvitePage = lazy(() => import("@/pages/ClaimInvitePage"));
const NfcActivatePage = lazy(() => import("@/pages/NfcActivatePage"));  // NFC owner-tags (isolated — remove with page + route)
const NfcFoundPage = lazy(() => import("@/pages/NfcFoundPage"));  // NFC owner-tags (isolated — remove with page + route)

function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="w-6 h-6 border-2 border-slate-300 border-t-slate-900 rounded-full animate-spin" />
    </div>
  );
}

function AppRouter() {
  return (
    <Suspense fallback={<PageLoader />}>
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/dashboard" element={<ProtectedRoute><HomePage /></ProtectedRoute>} />
      <Route path="/publish" element={<ProtectedRoute><PublishSelectPage /></ProtectedRoute>} />
      <Route path="/publish/:type" element={<ProtectedRoute><PublishItemPage /></ProtectedRoute>} />
      <Route path="/my-items" element={<ProtectedRoute><MyItemsPage /></ProtectedRoute>} />
      <Route path="/items/:itemId" element={<ProtectedRoute><ItemDetailPage /></ProtectedRoute>} />
      <Route path="/items/:itemId/edit" element={<ProtectedRoute><PublishItemPage /></ProtectedRoute>} />
      <Route path="/matches/:matchId" element={<ProtectedRoute><MatchDetailPage /></ProtectedRoute>} />
      <Route path="/chats" element={<ProtectedRoute><ChatListPage /></ProtectedRoute>} />
      <Route path="/chat/:matchId" element={<ProtectedRoute><ChatPage /></ProtectedRoute>} />
      <Route path="/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />
      <Route path="/payment/success" element={<ProtectedRoute><PaymentSuccessPage /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
      <Route path="/pickup/:token" element={<PickupPage />} />
      <Route path="/sign/:sessionToken" element={<SignaturePage />} />
      <Route path="/claim/:itemId" element={<ProtectedRoute><ClaimIdentifiedPage /></ProtectedRoute>} />
      <Route path="/claim-invite/:token" element={<ClaimInvitePage />} />
      <Route path="/activate" element={<ProtectedRoute><NfcActivatePage /></ProtectedRoute>} />
      <Route path="/found/:token" element={<ProtectedRoute><NfcFoundPage /></ProtectedRoute>} />
      <Route path="*" element={<LoginPage />} />
    </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <GoogleOAuthProvider clientId={import.meta.env.VITE_GOOGLE_CLIENT_ID}>
        <I18nProvider>
          <PublicConfigProvider>
            <BrowserRouter>
              <AuthProvider>
                <I18nErrorBoundary>
                  <AppRouter />
                </I18nErrorBoundary>
                <Toaster position="top-center" richColors />
              </AuthProvider>
            </BrowserRouter>
          </PublicConfigProvider>
        </I18nProvider>
      </GoogleOAuthProvider>
    </ErrorBoundary>
  );
}

export default App;
